import React, { useEffect, useState, useRef, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, PhoneIncoming, PhoneOff, MessageCircle, Bot, User, Sparkles, Play, Pause, RotateCcw, Brain, Link2, CheckCheck, Clock, Tag, Target } from 'lucide-react';
const STAGE_W = 1280;
const STAGE_H = 720;
// =================== Time markers ===================
const T = {
  CALL_IN: 0.5,
  ANSWER: 2.4,
  USER_MSG: 3.4,
  AI_BADGE_1: 5.3,
  AI_BADGE_2: 6.3,
  AI_BADGE_3: 7.2,
  CALL_END: 9.6,
  WA_APPEAR: 10.8,
  WA_USER_MSG: 12.2,
  AI_TYPING: 14.0,
  AI_REPLY: 15.4,
  SPLIT: 18.5,
  SPLIT_LINE: 20.0,
  SPLIT_LABELS: 21.0,
  IMPACT: 24.0,
  FINAL: 28.0
};
const TOTAL_DURATION = 33;
// =================== Captions ===================
function getCaption(t: number): {
  id: string;
  text: string;
} {
  if (t < T.AI_BADGE_1) return {
    id: 'a',
    text: 'A call begins…'
  };
  if (t < T.CALL_END) return {
    id: 'b',
    text: 'VEXU AI takes over.'
  };
  if (t < T.AI_REPLY) return {
    id: 'c',
    text: "The conversation doesn't restart…"
  };
  if (t < T.SPLIT) return {
    id: 'd',
    text: '…it continues.'
  };
  if (t < T.IMPACT) return {
    id: 'e',
    text: 'Same customer. Same context. One memory.'
  };
  return {
    id: 'f',
    text: 'One continuous experience.'
  };
}
// =================== Customer ===================
const CUSTOMER = {
  name: 'Ahmed Al-Mansoori',
  number: '+971 50 123 4567',
  initials: 'AM'
};
// =================== Main ===================
export const SLIDE_5_DURATION = TOTAL_DURATION;
export function PresentationSlide5({
  externalElapsed,
  hideChrome
}: {
  externalElapsed?: number;
  hideChrome?: boolean;
} = {}) {
  const isControlled = externalElapsed !== undefined;
  const [runKey, setRunKey] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [internalElapsed, setElapsed] = useState(0);
  const startedAtRef = useRef<number>(Date.now());
  const pausedAtRef = useRef<number>(0);
  useEffect(() => {
    if (isControlled) return;
    startedAtRef.current = Date.now();
    pausedAtRef.current = 0;
    setElapsed(0);
  }, [runKey, isControlled]);
  useEffect(() => {
    if (isControlled) return;
    if (!playing) {
      pausedAtRef.current = internalElapsed;
      return;
    }
    startedAtRef.current = Date.now() - pausedAtRef.current * 1000;
    let raf: number;
    const tick = () => {
      const t = (Date.now() - startedAtRef.current) / 1000;
      setElapsed(Math.min(t, TOTAL_DURATION));
      if (t < TOTAL_DURATION) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, runKey, isControlled]);
  const elapsed = isControlled ? Math.max(0, Math.min(externalElapsed!, TOTAL_DURATION)) : internalElapsed;
  const caption = getCaption(elapsed);
  return <div className="relative w-screen h-screen overflow-hidden bg-[#06080F] text-white font-sans flex items-center justify-center" data-id="element-1925">
      {/* Backgrounds */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#06080F] via-[#0A0F1F] to-[#06080F]" data-id="element-1926" />
      <motion.div className="absolute inset-0" animate={{
      background: ['radial-gradient(circle at 30% 40%, rgba(16,217,106,0.10) 0%, transparent 55%), radial-gradient(circle at 70% 70%, rgba(34,211,238,0.06) 0%, transparent 50%)', 'radial-gradient(circle at 70% 30%, rgba(16,217,106,0.10) 0%, transparent 55%), radial-gradient(circle at 30% 70%, rgba(34,211,238,0.06) 0%, transparent 50%)', 'radial-gradient(circle at 30% 40%, rgba(16,217,106,0.10) 0%, transparent 55%), radial-gradient(circle at 70% 70%, rgba(34,211,238,0.06) 0%, transparent 50%)']
    }} transition={{
      duration: 14,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-1927" />
      <div className="absolute inset-0 opacity-[0.10]" style={{
      backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.18) 1px, transparent 1px)',
      backgroundSize: '36px 36px'
    }} data-id="element-1928" />
      <div className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(ellipse at center, transparent 45%, rgba(0,0,0,0.7) 100%)'
    }} data-id="element-1929" />

      {/* Top controls */}
      {!hideChrome && <div className="absolute top-6 right-6 z-50 flex items-center gap-2" data-id="element-1930">
          <button onClick={() => setPlaying(p => !p)} className="flex items-center gap-2 px-4 py-2.5 bg-white/[0.05] hover:bg-white/[0.1] backdrop-blur-xl border border-white/10 rounded-full text-xs font-semibold text-white/80 hover:text-white transition-all" data-id="element-1931">
            {playing ? <>
                <Pause className="w-3.5 h-3.5" data-id="element-1932" /> Pause
              </> : <>
                <Play className="w-3.5 h-3.5" data-id="element-1933" /> Play
              </>}
          </button>
          <button onClick={() => {
        setRunKey(k => k + 1);
        setPlaying(true);
      }} className="flex items-center gap-2 px-4 py-2.5 bg-white/[0.05] hover:bg-white/[0.1] backdrop-blur-xl border border-white/10 rounded-full text-xs font-semibold text-white/80 hover:text-white transition-all" data-id="element-1934">
            <RotateCcw className="w-3.5 h-3.5" data-id="element-1935" /> Replay
          </button>
        </div>}

      {/* Chapter indicator (hidden) */}
      <motion.div initial={{
      opacity: 0,
      x: -12
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      duration: 0.6
    }} className="absolute top-6 left-6 z-50 hidden" data-id="element-1936">
        <div className="flex items-center gap-3" data-id="element-1937">
          <div className="w-8 h-px bg-emerald-400/70" data-id="element-1938" />
          <div data-id="element-1939">
            <div className="text-[10px] font-bold tracking-[0.3em] text-emerald-400 uppercase" data-id="element-1940">
              Slide 05 · Continuous Memory
            </div>
            <div className="text-sm font-display text-white/90 tracking-tight mt-0.5" data-id="element-1941">
              Calls + WhatsApp — one conversation
            </div>
          </div>
        </div>
      </motion.div>

      <Stage elapsed={elapsed} caption={caption} data-id="element-1942" />

      {/* Bottom progress bar */}
      {!hideChrome && <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-50 w-full max-w-2xl px-6" data-id="element-1943">
          <div className="h-1 rounded-full bg-white/[0.08] overflow-hidden" data-id="element-1944">
            <motion.div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-300" animate={{
          width: `${elapsed / TOTAL_DURATION * 100}%`
        }} transition={{
          duration: 0.1,
          ease: 'linear'
        }} data-id="element-1945" />
          </div>
          <div className="flex items-center justify-between text-[9px] font-bold tracking-[0.25em] text-white/45 uppercase mt-2.5" data-id="element-1946">
            <span data-id="element-1947">Slide 05 · Continuous Memory</span>
            <span className="font-mono" data-id="element-1948">
              {fmt(elapsed)} / {fmt(TOTAL_DURATION)}
            </span>
          </div>
        </div>}
    </div>;
}
function fmt(s: number) {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}
// =================== STAGE ===================
function Stage({
  elapsed,
  caption
}: {
  elapsed: number;
  caption: {
    id: string;
    text: string;
  };
}) {
  const callPhase = elapsed < T.CALL_END;
  const transitionPhase = elapsed >= T.CALL_END && elapsed < T.SPLIT;
  const splitPhase = elapsed >= T.SPLIT && elapsed < T.IMPACT;
  const impactPhase = elapsed >= T.IMPACT;
  return <motion.div initial={{
    opacity: 0,
    scale: 0.96
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 0.8,
    ease: [0.16, 1, 0.3, 1]
  }} className="relative" style={{
    width: 'min(100vw, calc(100vh * 16 / 9))',
    height: 'min(100vh, calc(100vw * 9 / 16))',
    maxWidth: '100vw',
    maxHeight: '100vh'
  }} data-id="element-1949">
      <div className="absolute inset-0" data-id="element-1950">
        <div className="absolute" style={{
        width: STAGE_W,
        height: STAGE_H,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%) scale(var(--stage-scale))',
        transformOrigin: 'center center'
      }} data-id="element-1951">
          <ScaleVar data-id="element-1952" />

          {/* TOP CAPTION */}
          <div className="absolute" style={{
          top: 24,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          zIndex: 6
        }} data-id="element-1953">
            <AnimatePresence mode="wait" data-id="element-1954">
              <motion.div key={caption.id} initial={{
              opacity: 0,
              y: 12,
              filter: 'blur(8px)'
            }} animate={{
              opacity: 1,
              y: 0,
              filter: 'blur(0px)'
            }} exit={{
              opacity: 0,
              y: -12,
              filter: 'blur(8px)'
            }} transition={{
              duration: 0.6,
              ease: [0.16, 1, 0.3, 1]
            }} className="px-7 py-2.5 rounded-full bg-white/[0.06] backdrop-blur-2xl border border-white/15 shadow-[0_8px_40px_rgba(0,0,0,0.5)]" data-id="element-1955">
                <p className="font-display text-lg md:text-xl text-white tracking-tight whitespace-nowrap" data-id="element-1956">
                  {caption.text}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* CHAPTER 1-2: CALL CARD */}
          <AnimatePresence data-id="element-1957">
            {callPhase && <motion.div key="call-card" initial={{
            opacity: 0,
            scale: 0.9,
            y: 16
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.94,
            x: -120,
            filter: 'blur(8px)'
          }} transition={{
            duration: 0.7,
            ease: [0.16, 1, 0.3, 1]
          }} className="absolute" style={{
            left: 360,
            top: 110,
            width: 560,
            zIndex: 5
          }} data-id="element-1958">
                <CallCard elapsed={elapsed} data-id="element-1959" />
              </motion.div>}
          </AnimatePresence>

          {/* CHAPTER 3-4: TRANSITION → CALL CARD SLIDES LEFT, WHATSAPP SLIDES IN RIGHT */}
          <AnimatePresence data-id="element-1960">
            {transitionPhase && <>
                {/* Faded call summary card on the left */}
                <motion.div key="call-summary" initial={{
              opacity: 0,
              x: 60,
              scale: 0.95
            }} animate={{
              opacity: 0.6,
              x: 0,
              scale: 0.92
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.7,
              ease: [0.16, 1, 0.3, 1]
            }} className="absolute" style={{
              left: 70,
              top: 130,
              width: 380,
              zIndex: 5
            }} data-id="element-1961">
                  <CallSummaryCard data-id="element-1962" />
                </motion.div>

                {/* Memory bridge between call and chat */}
                <MemoryBridge elapsed={elapsed} fromX={460} toX={680} y={350} data-id="element-1963" />

                {/* WhatsApp chat card on the right */}
                <motion.div key="wa-card" initial={{
              opacity: 0,
              x: 60,
              scale: 0.95
            }} animate={{
              opacity: 1,
              x: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              x: 40,
              scale: 0.95
            }} transition={{
              duration: 0.8,
              ease: [0.16, 1, 0.3, 1]
            }} className="absolute" style={{
              left: 690,
              top: 110,
              width: 520,
              zIndex: 5
            }} data-id="element-1964">
                  <WhatsAppCard elapsed={elapsed} data-id="element-1965" />
                </motion.div>
              </>}
          </AnimatePresence>

          {/* CHAPTER 5: SPLIT VIEW */}
          <AnimatePresence data-id="element-1966">
            {splitPhase && <>
                <motion.div key="split-call" initial={{
              opacity: 0,
              x: -30
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.8,
              ease: [0.16, 1, 0.3, 1]
            }} className="absolute" style={{
              left: 80,
              top: 110,
              width: 500,
              zIndex: 5
            }} data-id="element-1967">
                  <SplitCallTranscript data-id="element-1968" />
                </motion.div>

                <SplitMemoryLine elapsed={elapsed} fromX={580} toX={700} y={360} data-id="element-1969" />

                <motion.div key="split-wa" initial={{
              opacity: 0,
              x: 30
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.8,
              ease: [0.16, 1, 0.3, 1]
            }} className="absolute" style={{
              left: 700,
              top: 110,
              width: 500,
              zIndex: 5
            }} data-id="element-1970">
                  <SplitWhatsAppThread data-id="element-1971" />
                </motion.div>
              </>}
          </AnimatePresence>

          {/* CHAPTER 6: IMPACT */}
          <AnimatePresence data-id="element-1972">
            {impactPhase && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.9
          }} className="absolute inset-0 flex items-center justify-center" style={{
            background: 'radial-gradient(circle at center, rgba(6,8,15,0.94) 0%, rgba(6,8,15,0.99) 100%)',
            zIndex: 35
          }} data-id="element-1973">
                <ImpactReveal elapsed={elapsed} data-id="element-1974" />
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>;
}
// =================== CALL CARD ===================
function CallCard({
  elapsed
}: {
  elapsed: number;
}) {
  const ringing = elapsed < T.ANSWER;
  const callDuration = Math.max(0, elapsed - T.ANSWER);
  const showUserMsg = elapsed >= T.USER_MSG;
  const showBadge1 = elapsed >= T.AI_BADGE_1;
  const showBadge2 = elapsed >= T.AI_BADGE_2;
  const showBadge3 = elapsed >= T.AI_BADGE_3;
  const callEnding = elapsed >= T.CALL_END - 0.6;
  return <div className="relative w-full rounded-3xl bg-[#0A0F1F]/95 backdrop-blur-2xl border border-white/10 shadow-[0_24px_80px_rgba(0,0,0,0.7)] overflow-hidden" data-id="element-1975">
      <div className="absolute -inset-6 bg-emerald-500/10 rounded-3xl blur-2xl pointer-events-none" data-id="element-1976" />

      {/* Header */}
      <div className="relative flex items-center justify-between px-6 py-4 border-b border-white/[0.06]" data-id="element-1977">
        <div className="flex items-center gap-2" data-id="element-1978">
          <motion.div animate={{
          scale: ringing ? [1, 1.15, 1] : 1,
          opacity: ringing ? [1, 0.7, 1] : 1
        }} transition={{
          duration: 1.4,
          repeat: ringing ? Infinity : 0
        }} className={`w-2 h-2 rounded-full ${ringing ? 'bg-amber-400' : callEnding ? 'bg-red-400' : 'bg-emerald-400'}`} data-id="element-1979" />
          <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-white/70" data-id="element-1980">
            {ringing ? 'Incoming Call' : callEnding ? 'Ending…' : 'Live Call'}
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-[11px] font-mono text-white/50" data-id="element-1981">
          <Clock className="w-3 h-3" data-id="element-1982" />
          {fmtDur(callDuration)}
        </div>
      </div>

      {/* Caller */}
      <div className="relative px-6 pt-5 pb-4 flex items-center gap-4" data-id="element-1983">
        <div className="relative" data-id="element-1984">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500/30 to-emerald-700/20 border border-emerald-400/40 flex items-center justify-center" data-id="element-1985">
            <span className="text-lg font-bold text-white tracking-tight" data-id="element-1986">
              {CUSTOMER.initials}
            </span>
          </div>
          {ringing && <motion.div animate={{
          scale: [1, 1.5, 1],
          opacity: [0.6, 0, 0.6]
        }} transition={{
          duration: 1.6,
          repeat: Infinity
        }} className="absolute inset-0 rounded-2xl border border-emerald-400/60" data-id="element-1987" />}
        </div>
        <div className="flex-1 min-w-0" data-id="element-1988">
          <div className="text-[10px] font-bold tracking-[0.22em] text-white/45 uppercase mb-0.5" data-id="element-1989">
            Customer
          </div>
          <div className="text-base font-semibold text-white tracking-tight" data-id="element-1990">
            {CUSTOMER.name}
          </div>
          <div className="text-[12px] text-white/55 font-mono mt-0.5" data-id="element-1991">
            {CUSTOMER.number}
          </div>
        </div>
        <AnimatePresence mode="wait" data-id="element-1992">
          {ringing ? <motion.div key="ring" initial={{
          scale: 0.85,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.85,
          opacity: 0
        }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-400/10 border border-amber-400/30" data-id="element-1993">
              <PhoneIncoming className="w-3 h-3 text-amber-300" data-id="element-1994" />
              <span className="text-[10px] font-bold tracking-[0.18em] text-amber-200 uppercase" data-id="element-1995">
                Ringing
              </span>
            </motion.div> : callEnding ? <motion.div key="ending" initial={{
          scale: 0.85,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.85,
          opacity: 0
        }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500/15 border border-red-400/35" data-id="element-1996">
              <PhoneOff className="w-3 h-3 text-red-300" data-id="element-1997" />
              <span className="text-[10px] font-bold tracking-[0.18em] text-red-200 uppercase" data-id="element-1998">
                Ending
              </span>
            </motion.div> : <motion.div key="active" initial={{
          scale: 0.85,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.85,
          opacity: 0
        }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/35" data-id="element-1999">
              <Bot className="w-3 h-3 text-emerald-300" data-id="element-2000" />
              <span className="text-[10px] font-bold tracking-[0.18em] text-emerald-200 uppercase" data-id="element-2001">
                AI Handling
              </span>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Transcript */}
      <div className="relative px-6 pb-4 pt-2" data-id="element-2002">
        <div className="text-[10px] font-bold tracking-[0.22em] text-white/45 uppercase mb-3 flex items-center gap-1.5" data-id="element-2003">
          <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse" data-id="element-2004" />{' '}
          Live Transcript
        </div>
        <div className="space-y-2.5 min-h-[120px]" data-id="element-2005">
          <AnimatePresence data-id="element-2006">
            {showUserMsg && <motion.div key="user-msg" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="flex items-start gap-2.5" data-id="element-2007">
                <div className="w-6 h-6 rounded-lg bg-white/10 border border-white/10 flex items-center justify-center shrink-0" data-id="element-2008">
                  <User className="w-3 h-3 text-white/65" data-id="element-2009" />
                </div>
                <div className="flex-1" data-id="element-2010">
                  <div className="text-[10px] font-bold tracking-[0.18em] text-white/40 uppercase mb-1" data-id="element-2011">
                    Customer
                  </div>
                  <TypewriterText text='"Hi, I want to check my order status."' startTime={T.USER_MSG} elapsed={elapsed} duration={1.6} data-id="element-2012" />
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      {/* AI processing badges */}
      <div className="relative px-6 pb-5 pt-1 border-t border-white/[0.05] mt-2" data-id="element-2013">
        <div className="text-[10px] font-bold tracking-[0.22em] text-emerald-400 uppercase mb-3 flex items-center gap-1.5" data-id="element-2014">
          <Brain className="w-3 h-3" data-id="element-2015" /> AI Context
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-2016">
          <ContextBadge show={showBadge1} icon={<Target className="w-3 h-3" data-id="element-2018" />} label="Intent" value="Order Status" data-id="element-2017" />
          <ContextBadge show={showBadge2} icon={<Tag className="w-3 h-3" data-id="element-2020" />} label="Match" value="Customer #2841" data-id="element-2019" />
          <ContextBadge show={showBadge3} icon={<Sparkles className="w-3 h-3" data-id="element-2022" />} label="Status" value="Caller recognized" highlight data-id="element-2021" />
        </div>
      </div>
    </div>;
}
// =================== CALL SUMMARY (during transition phase) ===================
function CallSummaryCard() {
  return <div className="relative w-full rounded-2xl bg-[#0A0F1F]/95 backdrop-blur-xl border border-white/10 overflow-hidden" data-id="element-2023">
      <div className="px-5 py-3.5 border-b border-white/[0.06] flex items-center gap-2" data-id="element-2024">
        <div className="w-1.5 h-1.5 rounded-full bg-white/40" data-id="element-2025" />
        <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-white/55" data-id="element-2026">
          Call ended · 1:22
        </span>
      </div>
      <div className="px-5 py-4 flex items-center gap-3" data-id="element-2027">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500/25 to-emerald-700/15 border border-emerald-400/30 flex items-center justify-center" data-id="element-2028">
          <span className="text-[13px] font-bold text-white" data-id="element-2029">
            {CUSTOMER.initials}
          </span>
        </div>
        <div className="flex-1 min-w-0" data-id="element-2030">
          <div className="text-[13px] font-semibold text-white tracking-tight truncate" data-id="element-2031">
            {CUSTOMER.name}
          </div>
          <div className="text-[10px] text-white/50 font-mono" data-id="element-2032">
            {CUSTOMER.number}
          </div>
        </div>
      </div>
      <div className="px-5 pb-4 flex flex-wrap gap-1.5" data-id="element-2033">
        <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-400/25 text-[9px] font-semibold text-emerald-200 tracking-wide" data-id="element-2034">
          Order Status
        </span>
        <span className="px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/10 text-[9px] font-semibold text-white/65 tracking-wide" data-id="element-2035">
          Customer #2841
        </span>
      </div>
    </div>;
}
// =================== MEMORY BRIDGE (between call summary and WhatsApp) ===================
function MemoryBridge({
  elapsed,
  fromX,
  toX,
  y
}: {
  elapsed: number;
  fromX: number;
  toX: number;
  y: number;
}) {
  const visible = elapsed >= T.WA_APPEAR - 0.4;
  const length = toX - fromX;
  return <>
      <svg className="absolute pointer-events-none" width={STAGE_W} height={STAGE_H} viewBox={`0 0 ${STAGE_W} ${STAGE_H}`} style={{
      top: 0,
      left: 0,
      overflow: 'visible',
      zIndex: 2
    }} data-id="element-2036">
        <motion.line x1={fromX} y1={y} x2={toX} y2={y} stroke="#10D96A" strokeWidth={6} strokeOpacity={0.18} strokeLinecap="round" strokeDasharray={length} initial={{
        strokeDashoffset: length,
        opacity: 0
      }} animate={{
        strokeDashoffset: visible ? 0 : length,
        opacity: visible ? 1 : 0
      }} transition={{
        duration: 0.9,
        ease: [0.16, 1, 0.3, 1]
      }} data-id="element-2037" />
        <motion.line x1={fromX} y1={y} x2={toX} y2={y} stroke="#34D399" strokeWidth={2} strokeLinecap="round" strokeOpacity={0.9} strokeDasharray={length} initial={{
        strokeDashoffset: length,
        opacity: 0
      }} animate={{
        strokeDashoffset: visible ? 0 : length,
        opacity: visible ? 1 : 0
      }} transition={{
        duration: 0.9,
        ease: [0.16, 1, 0.3, 1]
      }} data-id="element-2038" />
        {/* Traveling memory packet */}
        {elapsed >= T.WA_APPEAR && <motion.circle r={4} fill="#fef3c7" animate={{
        cx: [fromX, toX],
        cy: [y, y],
        opacity: [0, 1, 1, 0]
      }} transition={{
        duration: 1.6,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-2039" />}
      </svg>
      {/* Memory badge */}
      <motion.div initial={{
      opacity: 0,
      scale: 0.85
    }} animate={{
      opacity: visible ? 1 : 0,
      scale: visible ? 1 : 0.85
    }} transition={{
      duration: 0.5,
      delay: visible ? 0.5 : 0
    }} className="absolute" style={{
      left: (fromX + toX) / 2,
      top: y,
      transform: 'translate(-50%, -50%)',
      zIndex: 8
    }} data-id="element-2040">
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#0A0F1F] border border-emerald-400/40 shadow-[0_0_18px_rgba(16,217,106,0.3)]" data-id="element-2041">
          <Brain className="w-3 h-3 text-emerald-300" data-id="element-2042" />
          <span className="text-[9px] font-bold tracking-[0.18em] text-emerald-200 uppercase" data-id="element-2043">
            Context
          </span>
        </div>
      </motion.div>
    </>;
}
// =================== WHATSAPP CARD ===================
function WhatsAppCard({
  elapsed
}: {
  elapsed: number;
}) {
  const showUserMsg = elapsed >= T.WA_USER_MSG;
  const aiTyping = elapsed >= T.AI_TYPING && elapsed < T.AI_REPLY;
  const showAiReply = elapsed >= T.AI_REPLY;
  return <div className="relative w-full rounded-3xl bg-[#0A0F1F]/95 backdrop-blur-2xl border border-white/10 shadow-[0_24px_80px_rgba(0,0,0,0.7)] overflow-hidden" data-id="element-2044">
      <div className="absolute -inset-6 bg-emerald-500/10 rounded-3xl blur-2xl pointer-events-none" data-id="element-2045" />

      {/* WA Header */}
      <div className="relative flex items-center justify-between px-5 py-4 border-b border-white/[0.06] bg-emerald-500/[0.04]" data-id="element-2046">
        <div className="flex items-center gap-3" data-id="element-2047">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500/30 to-emerald-700/20 border border-emerald-400/40 flex items-center justify-center" data-id="element-2048">
            <span className="text-[13px] font-bold text-white" data-id="element-2049">
              {CUSTOMER.initials}
            </span>
          </div>
          <div data-id="element-2050">
            <div className="text-[13px] font-semibold text-white tracking-tight" data-id="element-2051">
              {CUSTOMER.name}
            </div>
            <div className="text-[10px] text-white/50 font-mono flex items-center gap-1.5" data-id="element-2052">
              <span className="w-1 h-1 rounded-full bg-emerald-400" data-id="element-2053" />{' '}
              {CUSTOMER.number}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/35" data-id="element-2054">
          <MessageCircle className="w-3 h-3 text-emerald-300" data-id="element-2055" />
          <span className="text-[10px] font-bold tracking-[0.18em] text-emerald-200 uppercase" data-id="element-2056">
            WhatsApp
          </span>
        </div>
      </div>

      {/* Continuity banner */}
      <motion.div initial={{
      opacity: 0,
      y: -6
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5,
      delay: 0.15
    }} className="relative px-5 py-2 border-b border-white/[0.04] bg-gradient-to-r from-emerald-500/[0.06] to-transparent flex items-center gap-2" data-id="element-2057">
        <Link2 className="w-3 h-3 text-emerald-300" data-id="element-2058" />
        <span className="text-[10px] font-semibold tracking-wide text-emerald-100/85" data-id="element-2059">
          Continuing from your call · 14 sec ago
        </span>
      </motion.div>

      {/* Messages area */}
      <div className="relative px-5 py-5 space-y-3 min-h-[260px]" data-id="element-2060">
        <AnimatePresence data-id="element-2061">
          {showUserMsg && <motion.div key="wa-user" initial={{
          opacity: 0,
          y: 12,
          scale: 0.96
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} transition={{
          duration: 0.5,
          ease: [0.16, 1, 0.3, 1]
        }} className="flex justify-start" data-id="element-2062">
              <div className="max-w-[85%] px-4 py-2.5 rounded-2xl rounded-bl-md bg-white/[0.06] border border-white/10" data-id="element-2063">
                <div className="text-[13px] text-white tracking-tight leading-snug" data-id="element-2064">
                  Can you send me the tracking link?
                </div>
                <div className="text-[9px] text-white/35 mt-1 text-right font-mono" data-id="element-2065">
                  12:34
                </div>
              </div>
            </motion.div>}

          {aiTyping && <motion.div key="wa-typing" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -4
        }} className="flex justify-end" data-id="element-2066">
              <div className="px-4 py-3 rounded-2xl rounded-br-md bg-emerald-500/15 border border-emerald-400/30 flex items-center gap-1" data-id="element-2067">
                {[0, 1, 2].map(i => <motion.span key={i} animate={{
              y: [0, -3, 0],
              opacity: [0.4, 1, 0.4]
            }} transition={{
              duration: 0.9,
              repeat: Infinity,
              delay: i * 0.15
            }} className="w-1.5 h-1.5 rounded-full bg-emerald-300" data-id="element-2068" />)}
              </div>
            </motion.div>}

          {showAiReply && <motion.div key="wa-ai" initial={{
          opacity: 0,
          y: 12,
          scale: 0.96
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} transition={{
          duration: 0.55,
          ease: [0.16, 1, 0.3, 1]
        }} className="flex justify-end" data-id="element-2069">
              <div className="max-w-[88%] px-4 py-3 rounded-2xl rounded-br-md bg-gradient-to-br from-emerald-500/25 to-emerald-700/15 border border-emerald-400/40 shadow-[0_8px_24px_rgba(16,217,106,0.18)]" data-id="element-2070">
                <div className="flex items-center gap-1.5 mb-1.5" data-id="element-2071">
                  <Bot className="w-3 h-3 text-emerald-300" data-id="element-2072" />
                  <span className="text-[9px] font-bold tracking-[0.18em] text-emerald-200 uppercase" data-id="element-2073">
                    VEXU AI
                  </span>
                </div>
                <div className="text-[13px] text-white tracking-tight leading-snug" data-id="element-2074">
                  Here's your tracking link based on{' '}
                  <span className="bg-emerald-500/30 px-1 rounded text-emerald-100 font-semibold" data-id="element-2075">
                    your request earlier
                  </span>
                  .
                </div>
                <div className="mt-2 px-3 py-2 rounded-lg bg-[#0A0F1F]/60 border border-emerald-400/20 flex items-center gap-2" data-id="element-2076">
                  <Link2 className="w-3 h-3 text-emerald-300 shrink-0" data-id="element-2077" />
                  <span className="text-[11px] text-emerald-100/90 font-mono truncate" data-id="element-2078">
                    track.alfalah.ae/o/2841
                  </span>
                </div>
                <div className="flex items-center gap-1 mt-1.5 justify-end" data-id="element-2079">
                  <CheckCheck className="w-3 h-3 text-emerald-300" data-id="element-2080" />
                  <span className="text-[9px] text-emerald-200/60 font-mono" data-id="element-2081">
                    12:34
                  </span>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}
// =================== SPLIT VIEWS ===================
function SplitCallTranscript() {
  return <div className="relative w-full rounded-2xl bg-[#0A0F1F]/95 backdrop-blur-xl border border-white/10 shadow-[0_16px_60px_rgba(0,0,0,0.6)] overflow-hidden" data-id="element-2082">
      <div className="absolute -inset-4 bg-emerald-500/8 rounded-3xl blur-xl pointer-events-none" data-id="element-2083" />
      <div className="relative px-5 py-3.5 border-b border-white/[0.06] flex items-center justify-between" data-id="element-2084">
        <div className="flex items-center gap-2" data-id="element-2085">
          <Phone className="w-3.5 h-3.5 text-emerald-300" data-id="element-2086" />
          <span className="text-[11px] font-bold tracking-[0.22em] uppercase text-white/75" data-id="element-2087">
            Call Transcript
          </span>
        </div>
        <div className="text-[10px] text-white/40 font-mono" data-id="element-2088">12:32 · 1:22</div>
      </div>

      <div className="relative px-5 py-4 space-y-3" data-id="element-2089">
        <TranscriptLine speaker="Customer" text='"Hi, I want to check my order status."' you={false} data-id="element-2090" />
        <TranscriptLine speaker="VEXU AI" text='"Hello Ahmed, I see your recent order — let me pull up the details."' you={true} data-id="element-2091" />
        <TranscriptLine speaker="Customer" text={`"Great, thanks. I'll follow up later."`} you={false} data-id="element-2092" />
      </div>

      <div className="relative px-5 py-3 border-t border-white/[0.05] bg-white/[0.02] flex flex-wrap gap-1.5" data-id="element-2093">
        <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-400/25 text-[9px] font-semibold text-emerald-200 tracking-wide" data-id="element-2094">
          Order Status
        </span>
        <span className="px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/10 text-[9px] font-semibold text-white/65 tracking-wide" data-id="element-2095">
          Customer #2841
        </span>
        <span className="px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/10 text-[9px] font-semibold text-white/65 tracking-wide" data-id="element-2096">
          Resolved
        </span>
      </div>
    </div>;
}
function TranscriptLine({
  speaker,
  text,
  you
}: {
  speaker: string;
  text: string;
  you: boolean;
}) {
  return <div className="flex items-start gap-2.5" data-id="element-2097">
      <div className={`w-6 h-6 rounded-lg border flex items-center justify-center shrink-0 ${you ? 'bg-emerald-500/15 border-emerald-400/30' : 'bg-white/[0.05] border-white/10'}`} data-id="element-2098">
        {you ? <Bot className="w-3 h-3 text-emerald-300" data-id="element-2099" /> : <User className="w-3 h-3 text-white/65" data-id="element-2100" />}
      </div>
      <div className="flex-1 min-w-0" data-id="element-2101">
        <div className={`text-[10px] font-bold tracking-[0.18em] uppercase mb-0.5 ${you ? 'text-emerald-300/85' : 'text-white/40'}`} data-id="element-2102">
          {speaker}
        </div>
        <div className="text-[12px] text-white/85 tracking-tight leading-snug" data-id="element-2103">
          {text}
        </div>
      </div>
    </div>;
}
function SplitWhatsAppThread() {
  return <div className="relative w-full rounded-2xl bg-[#0A0F1F]/95 backdrop-blur-xl border border-white/10 shadow-[0_16px_60px_rgba(0,0,0,0.6)] overflow-hidden" data-id="element-2104">
      <div className="absolute -inset-4 bg-emerald-500/8 rounded-3xl blur-xl pointer-events-none" data-id="element-2105" />
      <div className="relative px-5 py-3.5 border-b border-white/[0.06] flex items-center justify-between" data-id="element-2106">
        <div className="flex items-center gap-2" data-id="element-2107">
          <MessageCircle className="w-3.5 h-3.5 text-emerald-300" data-id="element-2108" />
          <span className="text-[11px] font-bold tracking-[0.22em] uppercase text-white/75" data-id="element-2109">
            WhatsApp Chat
          </span>
        </div>
        <div className="text-[10px] text-white/40 font-mono" data-id="element-2110">
          12:34 · just now
        </div>
      </div>

      <div className="relative px-5 py-4 space-y-3" data-id="element-2111">
        <div className="flex justify-start" data-id="element-2112">
          <div className="max-w-[85%] px-3.5 py-2 rounded-2xl rounded-bl-md bg-white/[0.06] border border-white/10" data-id="element-2113">
            <div className="text-[12px] text-white/90 tracking-tight leading-snug" data-id="element-2114">
              Can you send me the tracking link?
            </div>
          </div>
        </div>
        <div className="flex justify-end" data-id="element-2115">
          <div className="max-w-[88%] px-3.5 py-2.5 rounded-2xl rounded-br-md bg-gradient-to-br from-emerald-500/25 to-emerald-700/15 border border-emerald-400/40" data-id="element-2116">
            <div className="flex items-center gap-1.5 mb-1" data-id="element-2117">
              <Bot className="w-2.5 h-2.5 text-emerald-300" data-id="element-2118" />
              <span className="text-[8px] font-bold tracking-[0.18em] text-emerald-200 uppercase" data-id="element-2119">
                VEXU AI
              </span>
            </div>
            <div className="text-[12px] text-white tracking-tight leading-snug" data-id="element-2120">
              Here's your tracking link based on{' '}
              <span className="bg-emerald-500/30 px-1 rounded text-emerald-100 font-semibold" data-id="element-2121">
                your request earlier
              </span>
              .
            </div>
            <div className="mt-1.5 px-2.5 py-1.5 rounded-md bg-[#0A0F1F]/60 border border-emerald-400/20 flex items-center gap-1.5" data-id="element-2122">
              <Link2 className="w-2.5 h-2.5 text-emerald-300 shrink-0" data-id="element-2123" />
              <span className="text-[10px] text-emerald-100/90 font-mono truncate" data-id="element-2124">
                track.alfalah.ae/o/2841
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="relative px-5 py-3 border-t border-white/[0.05] bg-white/[0.02] flex flex-wrap gap-1.5" data-id="element-2125">
        <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-400/25 text-[9px] font-semibold text-emerald-200 tracking-wide" data-id="element-2126">
          Order Status
        </span>
        <span className="px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/10 text-[9px] font-semibold text-white/65 tracking-wide" data-id="element-2127">
          Customer #2841
        </span>
        <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-400/25 text-[9px] font-semibold text-emerald-200 tracking-wide" data-id="element-2128">
          Continued
        </span>
      </div>
    </div>;
}
function SplitMemoryLine({
  elapsed,
  fromX,
  toX,
  y
}: {
  elapsed: number;
  fromX: number;
  toX: number;
  y: number;
}) {
  const visible = elapsed >= T.SPLIT_LINE;
  const labelsVisible = elapsed >= T.SPLIT_LABELS;
  const length = toX - fromX;
  return <>
      <svg className="absolute pointer-events-none" width={STAGE_W} height={STAGE_H} viewBox={`0 0 ${STAGE_W} ${STAGE_H}`} style={{
      top: 0,
      left: 0,
      overflow: 'visible',
      zIndex: 2
    }} data-id="element-2129">
        <motion.line x1={fromX} y1={y} x2={toX} y2={y} stroke="#10D96A" strokeWidth={8} strokeOpacity={0.18} strokeLinecap="round" strokeDasharray={length} initial={{
        strokeDashoffset: length,
        opacity: 0
      }} animate={{
        strokeDashoffset: visible ? 0 : length,
        opacity: visible ? 1 : 0
      }} transition={{
        duration: 1.0,
        ease: [0.16, 1, 0.3, 1]
      }} data-id="element-2130" />
        <motion.line x1={fromX} y1={y} x2={toX} y2={y} stroke="#6EE7B7" strokeWidth={2.5} strokeLinecap="round" strokeOpacity={0.95} strokeDasharray={length} initial={{
        strokeDashoffset: length,
        opacity: 0
      }} animate={{
        strokeDashoffset: visible ? 0 : length,
        opacity: visible ? 1 : 0
      }} transition={{
        duration: 1.0,
        ease: [0.16, 1, 0.3, 1]
      }} data-id="element-2131" />
        {visible && <motion.circle r={5} fill="#fef3c7" animate={{
        cx: [fromX, toX],
        cy: [y, y],
        opacity: [0, 1, 1, 0]
      }} transition={{
        duration: 1.8,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-2132" />}
      </svg>

      {/* Center memory chip + labels above/below */}
      <motion.div initial={{
      opacity: 0,
      y: -8
    }} animate={{
      opacity: labelsVisible ? 1 : 0,
      y: labelsVisible ? 0 : -8
    }} transition={{
      duration: 0.5
    }} className="absolute" style={{
      left: (fromX + toX) / 2,
      top: y - 60,
      transform: 'translate(-50%, 0)',
      zIndex: 8
    }} data-id="element-2133">
        <div className="px-3 py-1.5 rounded-full bg-[#0A0F1F]/95 border border-emerald-400/30 shadow-[0_4px_18px_rgba(16,217,106,0.2)]" data-id="element-2134">
          <span className="text-[10px] font-bold tracking-[0.18em] text-emerald-200 uppercase" data-id="element-2135">
            Same customer
          </span>
        </div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      scale: 0.85
    }} animate={{
      opacity: visible ? 1 : 0,
      scale: visible ? 1 : 0.85
    }} transition={{
      duration: 0.5,
      delay: visible ? 0.4 : 0
    }} className="absolute" style={{
      left: (fromX + toX) / 2,
      top: y,
      transform: 'translate(-50%, -50%)',
      zIndex: 8
    }} data-id="element-2136">
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0A0F1F] border border-emerald-400/45 shadow-[0_0_24px_rgba(16,217,106,0.4)]" data-id="element-2137">
          <Brain className="w-3.5 h-3.5 text-emerald-300" data-id="element-2138" />
          <span className="text-[10px] font-bold tracking-[0.18em] text-emerald-200 uppercase" data-id="element-2139">
            Shared Memory
          </span>
        </div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: labelsVisible ? 1 : 0,
      y: labelsVisible ? 0 : 8
    }} transition={{
      duration: 0.5,
      delay: labelsVisible ? 0.15 : 0
    }} className="absolute" style={{
      left: (fromX + toX) / 2,
      top: y + 50,
      transform: 'translate(-50%, 0)',
      zIndex: 8
    }} data-id="element-2140">
        <div className="px-3 py-1.5 rounded-full bg-[#0A0F1F]/95 border border-emerald-400/30 shadow-[0_4px_18px_rgba(16,217,106,0.2)]" data-id="element-2141">
          <span className="text-[10px] font-bold tracking-[0.18em] text-emerald-200 uppercase" data-id="element-2142">
            Same context
          </span>
        </div>
      </motion.div>
    </>;
}
// =================== Helpers ===================
function ContextBadge({
  show,
  icon,
  label,
  value,
  highlight
}: {
  show: boolean;
  icon: React.ReactNode;
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return <motion.div initial={{
    opacity: 0,
    scale: 0.85,
    y: 6
  }} animate={{
    opacity: show ? 1 : 0,
    scale: show ? 1 : 0.85,
    y: show ? 0 : 6
  }} transition={{
    duration: 0.5,
    ease: [0.16, 1, 0.3, 1]
  }} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border ${highlight ? 'bg-emerald-500/15 border-emerald-400/40 shadow-[0_0_16px_rgba(16,217,106,0.25)]' : 'bg-white/[0.04] border-white/10'}`} data-id="element-2143">
      <span className={highlight ? 'text-emerald-300' : 'text-white/55'} data-id="element-2144">
        {icon}
      </span>
      <span className="text-[9px] font-bold tracking-[0.18em] text-white/45 uppercase" data-id="element-2145">
        {label}
      </span>
      <span className={`text-[11px] font-semibold tracking-tight ${highlight ? 'text-emerald-100' : 'text-white/90'}`} data-id="element-2146">
        {value}
      </span>
    </motion.div>;
}
function TypewriterText({
  text,
  startTime,
  elapsed,
  duration
}: {
  text: string;
  startTime: number;
  elapsed: number;
  duration: number;
}) {
  const t = Math.max(0, elapsed - startTime);
  const ratio = Math.max(0, Math.min(1, t / duration));
  const n = Math.floor(text.length * ratio);
  const shown = text.slice(0, n);
  const typing = ratio < 1;
  return <div className="text-[13px] text-white/90 tracking-tight leading-snug" data-id="element-2147">
      {shown}
      {typing && <span className="inline-block w-[2px] h-3.5 bg-emerald-400 align-middle ml-0.5 animate-pulse" data-id="element-2148" />}
    </div>;
}
function fmtDur(s: number) {
  const mm = Math.floor(s / 60);
  const ss = Math.floor(s % 60);
  return `${mm}:${String(ss).padStart(2, '0')}`;
}
// =================== Impact takeover ===================
function ImpactReveal({
  elapsed
}: {
  elapsed: number;
}) {
  const t = elapsed - T.IMPACT;
  const tFinal = elapsed - T.FINAL;
  const lines = [{
    text: 'Calls.',
    show: 0.0
  }, {
    text: 'Messages.',
    show: 0.7
  }, {
    text: 'One continuous experience.',
    show: 1.6,
    big: true
  }];
  return <div className="text-center px-6" data-id="element-2149">
      <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.7
    }} className="flex items-center gap-2 justify-center mb-10" data-id="element-2150">
        <Sparkles className="w-3.5 h-3.5 text-emerald-400" data-id="element-2151" />
        <span className="text-[11px] font-bold tracking-[0.3em] text-emerald-400 uppercase" data-id="element-2152">
          One memory · Every channel
        </span>
        <Sparkles className="w-3.5 h-3.5 text-emerald-400" data-id="element-2153" />
      </motion.div>

      <div className="flex flex-col items-center gap-4 mb-12" data-id="element-2154">
        {lines.map((w, i) => {
        const visible = t >= w.show;
        return <motion.span key={i} initial={{
          opacity: 0,
          y: 24,
          scale: 0.9,
          filter: 'blur(10px)'
        }} animate={{
          opacity: visible ? 1 : 0,
          y: visible ? 0 : 24,
          scale: visible ? 1 : 0.9,
          filter: visible ? 'blur(0px)' : 'blur(10px)'
        }} transition={{
          duration: 0.85,
          ease: [0.16, 1, 0.3, 1]
        }} className={`font-display font-bold tracking-tight text-white ${w.big ? 'text-4xl md:text-6xl lg:text-7xl' : 'text-3xl md:text-5xl'}`} style={{
          textShadow: '0 0 30px rgba(16,217,106,0.4)'
        }} data-id="element-2155">
              {w.text}
            </motion.span>;
      })}
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 16,
      filter: 'blur(8px)'
    }} animate={{
      opacity: tFinal >= 0 ? 1 : 0,
      y: tFinal >= 0 ? 0 : 16,
      filter: tFinal >= 0 ? 'blur(0px)' : 'blur(8px)'
    }} transition={{
      duration: 0.9,
      ease: [0.16, 1, 0.3, 1]
    }} className="font-display text-2xl md:text-4xl tracking-tight italic" data-id="element-2156">
        <span style={{
        background: 'linear-gradient(135deg, #10D96A 0%, #6EE7B7 60%, #ffffff 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text'
      }} data-id="element-2157">
          VEXU remembers every conversation.
        </span>
      </motion.div>
    </div>;
}
// =================== ScaleVar ===================
function ScaleVar() {
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const scale = Math.min(w / STAGE_W, h / STAGE_H, 1);
      document.documentElement.style.setProperty('--stage-scale', String(scale));
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return null;
}